import clsx from 'clsx';
import {useEffect, useRef} from 'react';
import {
  Button,
  Drawer,
  IconClose,
  PageSectionContainer,
  Text,
  useDrawer,
} from '~/components';
import {DATA_TEST_IDS} from '~/lib/const';

function ProductQuestionsContent({id}: {id: string}) {
  const widgetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // @ts-ignore
    if (window.okeWidgetApi == null) {
      console.error('Okendo not found in window object.');
    } else {
      if (widgetRef.current == null) {
        console.error('Okendo question element not found.');
        return;
      }
      // Render the widget
      // @ts-ignore
      window.okeWidgetApi.initWidget(widgetRef.current);
    }
  }, []);

  return (
    <div
      ref={widgetRef}
      className={'px-5 xl:px-[97px] my-10'}
      id={'okendo-questions-widget'}
      data-oke-questions-widget
      data-oke-reviews-product-id={`shopify-${id}`}
    />
  );
}
function ProductQuestionsDrawer({
  id,
  productTitle,
  open,
  onClose,
}: {
  id: string;
  productTitle: string;
  open: boolean;
  onClose: () => void;
}) {
  return (
    <Drawer
      openFrom={'bottom'}
      classNamePanel="!w-screen"
      hideHeader
      open={open}
      onClose={onClose}
    >
      <div className={'shadow-all inline-block w-full'}>
        <PageSectionContainer className={'my-[11px]'} disableMarginY>
          <div className={'flex justify-between items-center'}>
            <Text as={'h1'} className={'!text-20/27 font-bold md:!text-20/27'}>
              {productTitle}
            </Text>
            <button
              data-testid={DATA_TEST_IDS.BUTTON('close-questions')}
              type={'button'}
              aria-label={'close drawer'}
              className={
                'flex justify-center items-center w-12 h-12 rounded-3xl bg-gray-200'
              }
              onClick={onClose}
            >
              <IconClose />
            </button>
          </div>
        </PageSectionContainer>
      </div>
      <ProductQuestionsContent id={id} />
    </Drawer>
  );
}

type QuestionAggregateType = {
  subscriberId_aggregateId: string;
  approvedQuestionCount: number;
  approvedAnswerCount: number;
};

function ProductQuestions({
  className,
  id,
  productTitle,
  data,
}: {
  className?: string;
  id: string;
  productTitle: string;
  data?: {questionAggregate: QuestionAggregateType};
}) {
  const {isOpen, openDrawer, closeDrawer} = useDrawer(false);
  const rawId = id.split('/').pop() as string;

  const {questionAggregate} = data ?? {};
  const {approvedQuestionCount} = questionAggregate ?? {};

  let headerTitle = 'Q&A';
  if (approvedQuestionCount != null && approvedQuestionCount > 0) {
    headerTitle += ` (${approvedQuestionCount})`;
  }

  return (
    <div
      data-testid={DATA_TEST_IDS.PRODUCT_QUESTIONS}
      className={clsx('flex flex-col justify-between py-6', className)}
    >
      <div>
        <Text
          className={
            'flex-1 text-22/22 md:text-28/28 font-header font-bold text-black'
          }
        >
          {headerTitle}
        </Text>
        <p className="mt-2 leading-tight">
          Ask a question and our experts will answer.
        </p>
      </div>
      <div className={'mt-[27px] md:mt-[30px]'}>
        <Button
          data-testid={DATA_TEST_IDS.BUTTON('see-all')}
          variant={'primary-inverse'}
          className={'py-2 px-[30px]'}
          onClick={openDrawer}
        >
          SEE ALL QUESTIONS
        </Button>
      </div>
      <ProductQuestionsDrawer
        id={rawId}
        productTitle={productTitle}
        open={isOpen}
        onClose={closeDrawer}
      />
    </div>
  );
}

export default ProductQuestions;
